import type {
  FindingsDecisionActionability,
  FindingsGroupBy,
  FindingsSearch,
  FindingsStatusFilter,
  FindingsView,
} from './findings-url';

export type OperatorViewId =
  | 'current'
  | 'safe-fixes'
  | 'policy-needed'
  | 'resolved'
  | 'ignored';

export type OperatorViewPreset = {
  id: OperatorViewId;
  label: string;
  description: string;
  patch: Pick<
    FindingsSearch,
    'status' | 'actionability' | 'groupBy' | 'sort' | 'view'
  >;
};

export type StatusPatchSummary = {
  requested: number;
  status: 'open' | 'ignored' | 'resolved' | string;
  changed: number;
  skipped: number;
  skippedReasons?: {
    already_status?: number;
  };
};

export type SingleStatusPatchResult = {
  status: 'open' | 'ignored' | 'resolved' | string;
  changed: boolean;
  skippedReason?: 'already_status';
};

export const OPERATOR_VIEW_PRESETS: OperatorViewPreset[] = [
  {
    id: 'current',
    label: 'Current queue',
    description: 'Open findings grouped by decision.',
    patch: { status: 'open', groupBy: 'decision' },
  },
  {
    id: 'safe-fixes',
    label: 'Safe fixes',
    description: 'Open findings ready for fix PRs.',
    patch: {
      status: 'open',
      actionability: 'safe_fix',
      groupBy: 'decision',
    },
  },
  {
    id: 'policy-needed',
    label: 'Policy needed',
    description: 'Open findings that need operator policy decisions.',
    patch: {
      status: 'open',
      actionability: 'policy_needed',
      groupBy: 'decision',
    },
  },
  {
    id: 'resolved',
    label: 'Resolved history',
    description: 'Historical findings that have been resolved.',
    patch: { status: 'resolved', sort: 'severity' },
  },
  {
    id: 'ignored',
    label: 'Ignored history',
    description: 'Historical findings suppressed by waivers.',
    patch: { status: 'ignored', sort: 'severity' },
  },
];

export function isOperatorViewActive(
  search: FindingsSearch,
  preset: OperatorViewPreset,
): boolean {
  return (
    normalizedStatus(search.status) === normalizedStatus(preset.patch.status) &&
    normalizedActionability(search.actionability) ===
      normalizedActionability(preset.patch.actionability) &&
    normalizedGroupBy(search.groupBy) ===
      normalizedGroupBy(preset.patch.groupBy) &&
    normalizedSort(search.sort) === normalizedSort(preset.patch.sort) &&
    normalizedView(search.view) === normalizedView(preset.patch.view)
  );
}

export function operatorViewPatch(
  preset: OperatorViewPreset,
): Partial<FindingsSearch> {
  return {
    status: undefined,
    actionability: undefined,
    policyStatus: undefined,
    decisionKind: undefined,
    decisionKey: undefined,
    groupBy: undefined,
    sort: undefined,
    page: undefined,
    ...preset.patch,
  };
}

export function formatStatusPatchSummary(result: StatusPatchSummary): {
  title: string;
  description: string;
} {
  const action = statusAction(result.status);
  const changed = plural(result.changed, action.changedNoun);
  const skipped = plural(result.skipped, action.skippedNoun);

  if (result.changed > 0 && result.skipped > 0) {
    return {
      title: `${action.pastTense} ${changed}`,
      description: `${skipped} already ${action.alreadyLabel}.`,
    };
  }

  if (result.changed > 0) {
    return {
      title: `${action.pastTense} ${changed}`,
      description: `${plural(result.requested, 'finding')} processed.`,
    };
  }

  return {
    title: `No findings changed`,
    description: `${skipped} already ${action.alreadyLabel}.`,
  };
}

function statusAction(status: StatusPatchSummary['status']): {
  pastTense: string;
  changedNoun: string;
  skippedNoun: string;
  alreadyLabel: string;
} {
  if (status === 'open') {
    return {
      pastTense: 'Reopened',
      changedNoun: 'finding',
      skippedNoun: 'finding',
      alreadyLabel: 'current',
    };
  }
  if (status === 'ignored') {
    return {
      pastTense: 'Ignored',
      changedNoun: 'finding',
      skippedNoun: 'finding',
      alreadyLabel: 'ignored',
    };
  }
  if (status === 'resolved') {
    return {
      pastTense: 'Resolved',
      changedNoun: 'finding',
      skippedNoun: 'finding',
      alreadyLabel: 'resolved',
    };
  }
  return {
    pastTense: 'Updated',
    changedNoun: 'finding',
    skippedNoun: 'finding',
    alreadyLabel: status || 'updated',
  };
}

function plural(count: number, singular: string): string {
  return `${count} ${count === 1 ? singular : `${singular}s`}`;
}

function normalizedStatus(
  value: FindingsStatusFilter | undefined,
): FindingsStatusFilter {
  return value ?? 'all';
}

function normalizedActionability(
  value: FindingsDecisionActionability | undefined,
): FindingsDecisionActionability | undefined {
  return value;
}

function normalizedGroupBy(
  value: FindingsGroupBy | undefined,
): FindingsGroupBy {
  return value ?? 'decision';
}

function normalizedSort(value: FindingsSearch['sort']): FindingsSearch['sort'] {
  return value ?? 'default';
}

function normalizedView(value: FindingsView | undefined): FindingsView {
  return value ?? 'all';
}
